import type React from "react";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography,
} from "@mui/material";
import {Close} from "@mui/icons-material";
import {creditsUsed} from "~/pages/supplier-360/tabs/SupplierValueChain/chainCredits";
import type {ChainCreditEntitlement} from "~/pages/supplier-360/tabs/SupplierValueChain/valueChainCreditTypes";
import {SummaryRows} from "./SummaryRows";

type ChainUnlockDialogProps = {
    entitlement: ChainCreditEntitlement;
    supplierName: string;
    isPending: boolean;
    onCancel: () => void;
    onConfirm: () => void;
};

/** "Use 1 credit" confirmation (`ChainUnlockModal` in the design) — the spend can't be undone. */
export const ChainUnlockDialog: React.FC<ChainUnlockDialogProps> = ({
                                                                        entitlement,
                                                                        supplierName,
                                                                        isPending,
                                                                        onCancel,
                                                                        onConfirm,
                                                                    }) => {
    const remaining = entitlement.credit_balance;
    const used = creditsUsed(entitlement);
    const closeIfIdle = () => {
        if (!isPending) onCancel();
    };

    return (
        <Dialog open onClose={closeIfIdle} fullWidth slotProps={{paper: {sx: {maxWidth: 530}}}} aria-labelledby="chain-unlock-title">
            <DialogTitle id="chain-unlock-title" sx={{pr: 6, fontSize: 16}}>
                {`Unlock value chain · ${supplierName}`}
                <IconButton onClick={closeIfIdle} title="Close" size="small" sx={{position: 'absolute', top: 12, right: 12}}>
                    <Close fontSize="small"/>
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Typography variant="body2">
                    This uses <b>1 company credit</b> and cannot be undone. {supplierName} then counts as one of your
                    mapped companies permanently, re-opening the chain later is free.
                </Typography>
                <SummaryRows
                    sx={{mt: 2}}
                    rows={[
                        {title: 'Balance now', value: `${remaining} of ${entitlement.credits_granted} left`},
                        {title: 'After unlocking', value: `${remaining - 1} left · ${used + 1} companies mapped`},
                    ]}
                />
            </DialogContent>
            <DialogActions sx={{px: 2.5, pb: 2.5}}>
                <Button variant="outlined" disabled={isPending} onClick={onCancel}>Cancel</Button>
                <Button
                    variant="contained"
                    loading={isPending}
                    onClick={onConfirm}
                >
                    Use 1 credit
                </Button>
            </DialogActions>
        </Dialog>
    );
};
